import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Alert } from 'antd'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useAccess } from '@/hooks/commons/useAccess'
import { usePermissions } from '@/hooks/commons/usePermissions'
import { useGetWhatsappBalance } from '@/hooks/whatsapp/useGetWhatsappBalance'
import { notificationPermissions } from '@/constants/permissions'
import { pageUrl } from '@/constants/url'
import { createRouteForLink } from '@/utils/helpers/urlHelper'
import { getBalanceState } from '@/modules/notifications/components/whatsappApi/helpers'

const dismissKey = 'waApiLowBalanceDismissedOn'

// Storage can be blocked (private mode); the banner then simply shows again
const readDismissedOn = () => {
  try {
    return localStorage.getItem(dismissKey)
  } catch {
    return null
  }
}

const writeDismissedOn = (day: string) => {
  try {
    localStorage.setItem(dismissKey, day)
  } catch {
    // ignore: dismissal is a convenience only
  }
}

// Shell banner when the shared WhatsApp Official balance is low or used up. Dismissible
// for the rest of the day; only for companies on WhatsApp API and users who manage
// notifications.
export const WhatsappApiLowBalanceBanner = () => {
  const { t } = useTranslation('notifications')
  const { waApiEnabled } = useAccess()
  const { havePermissionAccess } = usePermissions()
  const canSee =
    waApiEnabled === true &&
    havePermissionAccess({ permission: notificationPermissions.read })
  const { data } = useGetWhatsappBalance({ enabled: canSee })
  const today = dayjs().format('YYYY-MM-DD')
  const [dismissedOn, setDismissedOn] = useState(readDismissedOn)
  // The Notifications page already shows the same alert on its balance card
  const onNotificationsPage = useLocation().pathname.includes('/notifications')

  const state = getBalanceState(data)
  if (!canSee || dismissedOn === today || onNotificationsPage) return null
  if (state !== 'low' && state !== 'empty') return null

  const price = data?.pricePerMessage || 0
  return (
    <Alert
      banner
      closable
      type={state === 'empty' ? 'error' : 'warning'}
      className="mb-3"
      message={
        <span>
          {state === 'empty'
            ? t('notifications.waApi.balance.alert.empty')
            : t('notifications.waApi.balance.alert.low', {
                total: price ? Math.floor((data?.balance || 0) / price) : 0,
              })}{' '}
          <Link
            to={`${createRouteForLink(
              pageUrl.notifications,
            )}?tab=waApi&section=balance`}
          >
            {t('notifications.waApi.balance.topup')}
          </Link>
        </span>
      }
      onClose={() => {
        writeDismissedOn(today)
        setDismissedOn(today)
      }}
    />
  )
}
